import { Box, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import type { Moment } from "moment";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { PortalLifecycleStatus, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { isDpsScheme } from "../../buyer/wizard/utils/procedure";
import { formatShortDate, getTimeLeftMeta } from "../utils/displayFormatters";
import { deadlineLabelKey, effectiveDeadlineIso } from "../utils/effectiveDeadline";

type MilestoneStatus = "past" | "current" | "future";

interface Milestone {
  label: string;
  rawDate: string;
  date: string;
  status: MilestoneStatus;
}

export const EventProcessTimeline = ({
  event,
  now,
}: {
  event: PortalSourcingEventDetailDto;
  now: Moment;
}) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);

  const rawMilestones: Omit<Milestone, "status">[] = [
    event.publishedAt && { label: t("Announced"), rawDate: event.publishedAt, date: formatShortDate(event.publishedAt, locale) },
    event.requestDeadline && {
      label: isDpsScheme(event.procedureType)
        ? t("Deadline for the first intake")
        : t("Deadline for requests to participate"),
      rawDate: event.requestDeadline,
      date: formatShortDate(event.requestDeadline, locale),
    },
    event.dpsValidFrom && {
      label: t("The system opens"),
      rawDate: event.dpsValidFrom,
      date: formatShortDate(event.dpsValidFrom, locale),
    },
    event.dpsValidTo && {
      label: t("The system expires"),
      rawDate: event.dpsValidTo,
      date: formatShortDate(event.dpsValidTo, locale),
    },
    event.bidSubmissionDeadline && {
      label: t("Bid deadline"),
      rawDate: event.bidSubmissionDeadline,
      date: formatShortDate(event.bidSubmissionDeadline, locale),
    },
    event.contractStartDate && {
      label: t("Contract start"),
      rawDate: event.contractStartDate,
      date: formatShortDate(event.contractStartDate, locale),
    },
    event.contractEndDate && {
      label: t("Contract end"),
      rawDate: event.contractEndDate,
      date: formatShortDate(event.contractEndDate, locale),
    },
  ].filter((m): m is Omit<Milestone, "status"> => Boolean(m));

  const firstFutureIndex = rawMilestones.findIndex((m) => moment(m.rawDate).isAfter(now));
  const milestones: Milestone[] = rawMilestones.map((m, index) => ({
    ...m,
    status:
      index === firstFutureIndex ? "current" : moment(m.rawDate).isAfter(now) ? "future" : "past",
  }));

  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  // Null during the qualification window — the strip below disappears rather than counting
  // down to a deadline that doesn't exist yet.
  const deadlineIso = effectiveDeadlineIso(event);
  const timeLeftMeta = deadlineIso != null ? getTimeLeftMeta(deadlineIso, t, now) : null;

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder" boxShadow="sm">
      <CardBody px={{ base: "4", md: "6" }} py={{ base: "5", md: "6" }}>
        <Stack spacing="6">
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="smMuted"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {t("Process overview")}
          </Text>

          <Box display={{ base: "none", md: "block" }} position="relative">
            <Box position="absolute" top="6px" left="0" right="0" height="2px" bg="smBorder" />
            <Flex justify="space-between" position="relative">
              {milestones.map((milestone) => (
                <MilestoneItem key={milestone.label} milestone={milestone} />
              ))}
            </Flex>
          </Box>

          <Stack display={{ base: "flex", md: "none" }} spacing="3">
            {milestones.map((milestone) => (
              <Flex key={milestone.label} align="center" gap="3">
                <MilestoneDot status={milestone.status} />
                <Text
                  flex="1"
                  fontSize="sm"
                  fontWeight={milestone.status === "current" ? "semibold" : "normal"}
                  color={milestone.status === "current" ? "smPrimary" : "smText"}
                >
                  {milestone.label}
                </Text>
                <Text fontSize="sm" color="smMuted">
                  {milestone.date}
                </Text>
              </Flex>
            ))}
          </Stack>

          {isOpen && deadlineIso != null && timeLeftMeta != null && (
            <Card variant="outline" rounded="lg" bg="smPrimarySofter" borderColor="smPrimarySoft">
              <CardBody px="4" py="3">
                <Flex align="center" gap="3">
                  <Flex
                    width="36px"
                    height="36px"
                    rounded="full"
                    bg="smBackground"
                    align="center"
                    justify="center"
                    flexShrink="0"
                  >
                    <Icon as={FiClock} boxSize="4" color="smPrimary" />
                  </Flex>
                  <Stack spacing="0" flex="1">
                    <Text fontSize="sm" fontWeight="semibold" color="smText">
                      {t(deadlineLabelKey(event))}
                    </Text>
                    <Text fontSize="xs" color="smMuted">
                      {formatShortDate(deadlineIso, locale)}
                    </Text>
                  </Stack>
                  <Text fontSize="sm" fontWeight="semibold" color={timeLeftMeta.color}>
                    {timeLeftMeta.label}
                  </Text>
                </Flex>
              </CardBody>
            </Card>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
};

const MilestoneItem = ({ milestone }: { milestone: Milestone }) => (
  <Stack spacing="2" align="center" position="relative" zIndex="1" px="2" bg="smBackground">
    <MilestoneDot status={milestone.status} />
    <Text
      fontSize="sm"
      fontWeight={milestone.status === "current" ? "semibold" : "normal"}
      color={milestone.status === "current" ? "smPrimary" : "smText"}
      textAlign="center"
    >
      {milestone.label}
    </Text>
    <Text fontSize="xs" color="smMuted" textAlign="center">
      {milestone.date}
    </Text>
  </Stack>
);

const MilestoneDot = ({ status }: { status: MilestoneStatus }) => (
  <Box
    width="14px"
    height="14px"
    rounded="full"
    flexShrink="0"
    bg={status === "future" ? "smBackground" : "smPrimary"}
    border="2px solid"
    borderColor={status === "future" ? "smBorder" : "smPrimary"}
    boxShadow={status === "current" ? "0 0 0 4px var(--chakra-colors-smPrimarySofter)" : undefined}
  />
);
